import React, { useState } from "react";
import { useNavigate } from "react-router-dom";
import {
    Box,
    Container,
    Card,
    CardContent,
    TextField,
    Button,
    Typography,
    InputAdornment,
    IconButton,
    Divider,
    Alert,
    Snackbar
} from "@mui/material";
import { styled } from "@mui/material/styles";
import { Visibility, VisibilityOff, Email, Lock } from "@mui/icons-material";
import logo from "../assets/colored_logo.png";

// Styled components
const LoginContainer = styled(Container)(({ theme }) => ({
    display: "flex",
    flexDirection: "column",
    justifyContent: "center",
    alignItems: "center",
    minHeight: "100vh",
    backgroundColor: theme.palette.background.default,
    padding: theme.spacing(3),
}));

const StyledCard = styled(Card)(({ theme }) => ({
    width: "100%",
    maxWidth: 450,
    borderRadius: theme.spacing(2),
    boxShadow: "0 8px 40px rgba(0, 0, 0, 0.12)",
    overflow: "visible",
    position: "relative",
}));

const LogoContainer = styled(Box)(({ theme }) => ({
    display: "flex",
    justifyContent: "center",
    marginBottom: theme.spacing(4),
}));

const Form = styled("form")(({ theme }) => ({
    display: "flex",
    flexDirection: "column",
    gap: theme.spacing(3),
}));

const SubmitButton = styled(Button)(({ theme }) => ({
    padding: theme.spacing(1.5),
    borderRadius: theme.spacing(1),
    textTransform: "none",
    fontWeight: 600,
    fontSize: "1rem",
    boxShadow: "0 4px 12px rgba(0, 0, 0, 0.1)",
    marginTop: theme.spacing(2),
}));

const VersionText = styled(Typography)(({ theme }) => ({
    color: theme.palette.text.secondary,
    marginTop: theme.spacing(3),
    fontSize: "0.875rem",
}));

const LoginScreen = () => {
    const [email, setEmail] = useState("");
    const [password, setPassword] = useState("");
    const [showPassword, setShowPassword] = useState(false);
    const [error, setError] = useState("");
    const [openSnackbar, setOpenSnackbar] = useState(false);
    const navigate = useNavigate();

    const handleClickShowPassword = () => {
        setShowPassword(!showPassword);
    };

    const handleLogin = async (e) => {
        e.preventDefault();
        setError("");

        if (!email || !password) {
            setError("Please enter your email and password");
            return;
        }

        try {
            const myHeaders = new Headers();
            myHeaders.append("Content-Type", "application/x-www-form-urlencoded");

            const urlencoded = new URLSearchParams();
            urlencoded.append("email", email);
            urlencoded.append("password", password);

            const requestOptions = {
                method: 'POST',
                headers: myHeaders,
                body: urlencoded,
                redirect: 'follow'
            };

            const response = await fetch("http://biometick.com/mobile/login", requestOptions);
            const result = await response.json();

            if (result.success) {
                localStorage.setItem('userID', result.user._id);
                localStorage.setItem('token', result.token);
                setOpenSnackbar(true);
                setTimeout(() => window.location.reload(), 1500);
            } else {
                setError(result.message || "Login failed. Please check your credentials.");
            }
        } catch (error) {
            console.error('Error during login:', error);
            setError("An error occurred. Please try again later.");
        }
    };

    return (
        <LoginContainer maxWidth={false}>
            <StyledCard elevation={0}>
                <CardContent sx={{ padding: 4 }}>
                    <LogoContainer>
                        <img src={logo} alt="Company Logo" style={{ height: 80, objectFit: 'contain' }} />
                    </LogoContainer>

                    <Typography variant="h5" component="h1" gutterBottom align="center" fontWeight="500">
                        Welcome Back
                    </Typography>

                    <Typography variant="body2" color="textSecondary" align="center" sx={{ mb: 4 }}>
                        Sign in to continue to your account
                    </Typography>

                    {error && (
                        <Alert severity="error" sx={{ mb: 3 }}>
                            {error}
                        </Alert>
                    )}

                    <Form onSubmit={handleLogin}>
                        <TextField
                            id="email"
                            label="Email"
                            variant="outlined"
                            fullWidth
                            value={email}
                            onChange={(e) => setEmail(e.target.value)}
                            InputProps={{
                                startAdornment: (
                                    <InputAdornment position="start">
                                        <Email color="action" />
                                    </InputAdornment>
                                ),
                            }}
                        />

                        <TextField
                            id="password"
                            label="Password"
                            type={showPassword ? "text" : "password"}
                            variant="outlined"
                            fullWidth
                            value={password}
                            onChange={(e) => setPassword(e.target.value)}
                            InputProps={{
                                startAdornment: (
                                    <InputAdornment position="start">
                                        <Lock color="action" />
                                    </InputAdornment>
                                ),
                                endAdornment: (
                                    <InputAdornment position="end">
                                        <IconButton
                                            aria-label="toggle password visibility"
                                            onClick={handleClickShowPassword}
                                            edge="end"
                                        >
                                            {showPassword ? <VisibilityOff /> : <Visibility />}
                                        </IconButton>
                                    </InputAdornment>
                                ),
                            }}
                        />

                        <Box sx={{ display: "flex", justifyContent: "flex-end" }}>
                            <Typography variant="body2" color="primary" sx={{ cursor: "pointer" }}>
                                Forgot password?
                            </Typography>
                        </Box>

                        <SubmitButton
                            variant="contained"
                            color="primary"
                            fullWidth
                            type="submit"
                            disableElevation
                        >
                            Sign In
                        </SubmitButton>
                    </Form>

                    <Box sx={{ mt: 3, mb: 2 }}>
                        <Divider>
                            <Typography variant="body2" color="textSecondary">
                                Don't have an account?
                            </Typography>
                        </Divider>
                    </Box>

                    <Button
                        variant="outlined"
                        color="primary"
                        fullWidth
                        sx={{
                            padding: 1.5,
                            borderRadius: 1,
                            textTransform: "none",
                            fontWeight: 500,
                        }}
                    >
                        Create Account
                    </Button>
                </CardContent>
            </StyledCard>

            <VersionText>
                Version: 2.1
            </VersionText>

            <Snackbar
                open={openSnackbar}
                autoHideDuration={3000}
                onClose={() => setOpenSnackbar(false)}
            >
                <Alert severity="success" sx={{ width: '100%' }}>
                    Login successful! Redirecting...
                </Alert>
            </Snackbar>
        </LoginContainer>
    );
};

export default LoginScreen;